/* eslint-disable max-lines -- one layer component per well-status combination: the file is a
   flat list of <Layer> declarations whose length tracks the status vocabulary, and splitting it
   would scatter paint rules that are only comprehensible side by side. */
import { useAtomValue } from 'jotai';
import type { LinePaint } from 'mapbox-gl';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import {
  createMapSourceAtom,
  mapWellPaintAtom,
  mapWellStatusFilterAtom,
} from './atoms';
import { MapUserConfigAtom } from './types';
import { circlePaint, textSizeInterpolate } from './utils';
import { mapColors } from './map-colors';
import { useMemo } from 'react';
import {
  prePermitHexagonLayout,
  prePermitHexagonPaint,
  usePrePermitHexagonImage,
} from './pre-permit-hexagon';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';

const WellsV2Layer = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const wellsV2SourceAtom = useMemo(() => createMapSourceAtom('wellsV2'), []);
  const wellsV2Source = useAtomValue(wellsV2SourceAtom);
  const mapConfig = useAtomValue(mapConfigAtom);
  const wellPaint = useAtomValue(mapWellPaintAtom);
  // The Create New Well statuses draw as one hollow hexagon (INS-13300).
  usePrePermitHexagonImage();
  const wellStatusFilter = useAtomValue(mapWellStatusFilterAtom);

  // The scheme below, with any status a colour-by rule has recoloured swapped in. Every
  // well layer — the survey and stick lines and the per-status circles — reads this one
  // object, so overriding it recolours the map's own wells rather than laying another
  // layer over them. Null paint, or one keyed on a property the tiles do not carry,
  // leaves the scheme exactly as it was.
  const colors = useMemo(() => {
    // From the shared colour file (enums/well_status_colors.json, INS-13300), so the map
    // agrees with the status pills, the legends and the colour-by defaults.
    const scheme = {
      producing: WELL_STATUS_COLOR_MAP.producing,
      duc: WELL_STATUS_COLOR_MAP.duc,
      permit: WELL_STATUS_COLOR_MAP.permit,
      shutIn: WELL_STATUS_COLOR_MAP.shut_in,
      expiredPermit: WELL_STATUS_COLOR_MAP.expired_permit,
      inactive: WELL_STATUS_COLOR_MAP.inactive,
      prePermit: WELL_STATUS_COLOR_MAP.pre_permit,
      apd: WELL_STATUS_COLOR_MAP.apd,
      proposal: WELL_STATUS_COLOR_MAP.proposal,
      planned: WELL_STATUS_COLOR_MAP.planned,
    };
    if (!wellPaint || wellPaint.property !== 'status') {
      return scheme;
    }
    const painted = wellPaint.colors;
    return {
      producing: painted.producing ?? scheme.producing,
      duc: painted.duc ?? scheme.duc,
      permit: painted.permit ?? scheme.permit,
      shutIn: painted.shut_in ?? scheme.shutIn,
      expiredPermit: painted.expired_permit ?? scheme.expiredPermit,
      inactive: painted.inactive ?? scheme.inactive,
      prePermit: painted.pre_permit ?? scheme.prePermit,
      apd: painted.apd ?? scheme.apd,
      proposal: painted.proposal ?? scheme.proposal,
      planned: painted.planned ?? scheme.planned,
    };
  }, [wellPaint]);

  // The user's own per-status toggles, narrowed by any status filter in force. Combined
  // rather than replaced: a filter says which wells are of interest, a toggle says which
  // are drawn at all, and a status either one excludes stays out. Every layer below
  // reads these, so narrowing here reaches all of them.
  const wellsV2Options = useMemo(() => {
    const options = mapConfig.wellsV2.options;
    if (!wellStatusFilter) {
      return options;
    }
    /**
     * One toggle, narrowed by the status filter. A helper rather than `&&` at each of the
     * twenty lines below: the test is identical every time, and twenty copies of it read as
     * twenty decisions when there is only one.
     * @param {boolean} on Whether the user has this toggle on.
     * @param {string} status The stored status it draws.
     * @return {boolean} Whether to draw it.
     */
    const narrow = (on: boolean, status: string) =>
      on && wellStatusFilter.includes(status);
    return {
      ...options,
      horizontalPermit: narrow(options.horizontalPermit, 'permit'),
      horizontalDuc: narrow(options.horizontalDuc, 'duc'),
      horizontalProducing: narrow(options.horizontalProducing, 'producing'),
      horizontalShutIn: narrow(options.horizontalShutIn, 'shut_in'),
      horizontalExpiredPermit: narrow(
        options.horizontalExpiredPermit,
        'expired_permit'
      ),
      horizontalInactive: narrow(options.horizontalInactive, 'inactive'),
      horizontalPrePermit: narrow(options.horizontalPrePermit, 'pre_permit'),
      horizontalApd: narrow(options.horizontalApd, 'apd'),
      horizontalProposal: narrow(options.horizontalProposal, 'proposal'),
      horizontalPlanned: narrow(options.horizontalPlanned, 'planned'),
      verticalPermit: narrow(options.verticalPermit, 'permit'),
      verticalDuc: narrow(options.verticalDuc, 'duc'),
      verticalProducing: narrow(options.verticalProducing, 'producing'),
      verticalShutIn: narrow(options.verticalShutIn, 'shut_in'),
      verticalExpiredPermit: narrow(
        options.verticalExpiredPermit,
        'expired_permit'
      ),
      verticalInactive: narrow(options.verticalInactive, 'inactive'),
      verticalPrePermit: narrow(options.verticalPrePermit, 'pre_permit'),
      verticalApd: narrow(options.verticalApd, 'apd'),
      verticalProposal: narrow(options.verticalProposal, 'proposal'),
      verticalPlanned: narrow(options.verticalPlanned, 'planned'),
    };
  }, [mapConfig, wellStatusFilter]);

  // Return null if wellsV2Source is undefined to prevent the error
  if (!wellsV2Source) {
    return null;
  }

  // Check if any category is enabled
  const isHorizontalEnabled = wellsV2Options.horizontal;
  const isVerticalEnabled = wellsV2Options.vertical;

  // If no categories are enabled, don't show any wells
  if (!isHorizontalEnabled && !isVerticalEnabled) {
    return null;
  }

  // Create filters for horizontal and vertical well types
  const horizontalFilter = [
    'in',
    'hole_direction',
    'HORIZONTAL',
    'HORIZONTAL MINING BOREHOLE',
  ];
  const verticalFilter = [
    'in',
    'hole_direction',
    'VERTICAL',
    'DIRECTIONAL',
    'PINNATE',
  ];

  // Create status filters
  const permitFilter = ['==', 'status', 'permit'];
  const ducFilter = ['==', 'status', 'duc'];
  const producingFilter = ['==', 'status', 'producing'];
  const shutInFilter = ['==', 'status', 'shut_in'];
  const expiredPermitFilter = ['==', 'status', 'expired_permit'];
  const inactiveFilter = ['==', 'status', 'inactive'];
  // Lowercase, like every token above: the wells-v2 PG tiles carry `wells.status`
  // verbatim. The Elastic MVT source upcases its copy — do not borrow those tokens.
  const prePermitFilter = ['==', 'status', 'pre_permit'];
  const apdFilter = ['==', 'status', 'apd'];
  const proposalFilter = ['==', 'status', 'proposal'];
  const plannedFilter = ['==', 'status', 'planned'];

  // The survey layers' filters: direction and the enabled statuses.
  const horizontalSurveyFilter = [
    'all',
    horizontalFilter,
    [
      'any',
      ...(wellsV2Options.horizontalPermit ? [permitFilter] : []),
      ...(wellsV2Options.horizontalDuc ? [ducFilter] : []),
      ...(wellsV2Options.horizontalProducing ? [producingFilter] : []),
      ...(wellsV2Options.horizontalShutIn ? [shutInFilter] : []),
      ...(wellsV2Options.horizontalExpiredPermit ? [expiredPermitFilter] : []),
      ...(wellsV2Options.horizontalInactive ? [inactiveFilter] : []),
      ...(wellsV2Options.horizontalPrePermit ? [prePermitFilter] : []),
      ...(wellsV2Options.horizontalApd ? [apdFilter] : []),
      ...(wellsV2Options.horizontalProposal ? [proposalFilter] : []),
      ...(wellsV2Options.horizontalPlanned ? [plannedFilter] : []),
    ],
  ];
  const verticalSurveyFilter = [
    'all',
    verticalFilter,
    [
      'any',
      ...(wellsV2Options.verticalPermit ? [permitFilter] : []),
      ...(wellsV2Options.verticalDuc ? [ducFilter] : []),
      ...(wellsV2Options.verticalProducing ? [producingFilter] : []),
      ...(wellsV2Options.verticalShutIn ? [shutInFilter] : []),
      ...(wellsV2Options.verticalExpiredPermit ? [expiredPermitFilter] : []),
      ...(wellsV2Options.verticalInactive ? [inactiveFilter] : []),
      ...(wellsV2Options.verticalPrePermit ? [prePermitFilter] : []),
      ...(wellsV2Options.verticalApd ? [apdFilter] : []),
      ...(wellsV2Options.verticalProposal ? [proposalFilter] : []),
      ...(wellsV2Options.verticalPlanned ? [plannedFilter] : []),
    ],
  ];
  // survey_estimated: the line was built rather than measured, an operator's correction
  // carried onto a well with no filed survey. On the survey layer only, and absent on a
  // tile cut before it existed; an absent value reads as a measured survey.
  const measuredSurvey = ['!=', 'survey_estimated', true];
  const estimatedSurvey = ['==', 'survey_estimated', true];
  const surveyPaint: LinePaint = {
    'line-color': [
      'match',
      ['get', 'status'],
      'producing',
      colors.producing,
      'duc',
      colors.duc,
      'permit',
      colors.permit,
      'shut_in',
      colors.shutIn,
      'expired_permit',
      colors.expiredPermit,
      'inactive',
      colors.inactive,
      'pre_permit',
      colors.prePermit,
      'apd',
      colors.apd,
      'proposal',
      colors.proposal,
      'planned',
      colors.planned,
      mapColors.surveyFill, // default color if none match
    ],
    'line-width': 1.5,
  };
  // The stick's dash, so an estimated line reads the same wherever it comes from.
  const estimatedSurveyPaint: LinePaint = {
    ...surveyPaint,
    'line-dasharray': [6, 2],
  };

  // Helper function to create combined filters
  const createCombinedFilter = (
    directionFilter: any[],
    statusFilter: any[]
  ) => {
    return ['all', directionFilter, statusFilter];
  };

  return (
    <LayerBand band={LAYER_ORDER.wells}>
      <Source id="wells-v2" type="vector" url={wellsV2Source}>
        {/* Survey lines for horizontal wells */}
        {isHorizontalEnabled && (
          <Layer
            id="wells-v2-horizontal-survey"
            type="line"
            source-layer="survey"
            filter={['all', horizontalSurveyFilter, measuredSurvey]}
            paint={surveyPaint}
          />
        )}
        {/* A survey line with no filed survey behind it is an estimate, drawn dashed
            like a stick. A separate layer, since line-dasharray takes no data expression. */}
        {isHorizontalEnabled && (
          <Layer
            id="wells-v2-horizontal-survey-estimated"
            type="line"
            source-layer="survey"
            filter={['all', horizontalSurveyFilter, estimatedSurvey]}
            paint={estimatedSurveyPaint}
          />
        )}

        {/* Well names along survey lines for horizontal wells - always show */}
        {isHorizontalEnabled && (
          <Layer
            id="wells-v2-horizontal-survey-names"
            type="symbol"
            source-layer="survey"
            filter={[
              'all',
              horizontalFilter,
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.horizontalPermit ? [permitFilter] : []),
                ...(wellsV2Options.horizontalDuc ? [ducFilter] : []),
                ...(wellsV2Options.horizontalProducing
                  ? [producingFilter]
                  : []),
                ...(wellsV2Options.horizontalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.horizontalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.horizontalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.horizontalPrePermit
                  ? [prePermitFilter]
                  : []),
                ...(wellsV2Options.horizontalApd ? [apdFilter] : []),
                ...(wellsV2Options.horizontalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.horizontalPlanned ? [plannedFilter] : []),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />
        )}

        {/* Stick lines for horizontal wells that don't have survey */}
        {isHorizontalEnabled && (
          <Layer
            id="wells-v2-stick"
            type="line"
            source-layer="stick"
            filter={[
              'all',
              horizontalFilter,
              ['==', 'has_survey', false],
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.horizontalPermit ? [permitFilter] : []),
                ...(wellsV2Options.horizontalDuc ? [ducFilter] : []),
                ...(wellsV2Options.horizontalProducing
                  ? [producingFilter]
                  : []),
                ...(wellsV2Options.horizontalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.horizontalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.horizontalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.horizontalPrePermit
                  ? [prePermitFilter]
                  : []),
                ...(wellsV2Options.horizontalApd ? [apdFilter] : []),
                ...(wellsV2Options.horizontalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.horizontalPlanned ? [plannedFilter] : []),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                mapColors.stickFill, // default color if none match
              ],
              'line-width': 1.5,
              'line-dasharray': [6, 2],
            }}
          />
        )}

        {/* Well names along stick lines for horizontal wells - always show */}
        {isHorizontalEnabled && (
          <Layer
            id="wells-v2-horizontal-stick-names"
            type="symbol"
            source-layer="stick"
            filter={[
              'all',
              horizontalFilter,
              ['==', 'has_survey', false],
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.horizontalPermit ? [permitFilter] : []),
                ...(wellsV2Options.horizontalDuc ? [ducFilter] : []),
                ...(wellsV2Options.horizontalProducing
                  ? [producingFilter]
                  : []),
                ...(wellsV2Options.horizontalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.horizontalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.horizontalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.horizontalPrePermit
                  ? [prePermitFilter]
                  : []),
                ...(wellsV2Options.horizontalApd ? [apdFilter] : []),
                ...(wellsV2Options.horizontalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.horizontalPlanned ? [plannedFilter] : []),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />
        )}

        {/* Horizontal Wells by Status */}
        {isHorizontalEnabled && wellsV2Options.horizontalPermit && (
          <Layer
            id="wells-v2-horizontal-permit"
            type="circle"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, permitFilter)}
            paint={circlePaint(colors.permit)} // Pink for permit
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalPermit && (
          <Layer
            id="wells-v2-horizontal-permit-bottom"
            type="circle"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, permitFilter)}
            paint={circlePaint(colors.permit)} // Pink for permit
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalDuc && (
          <Layer
            id="wells-v2-horizontal-duc"
            type="circle"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, ducFilter)}
            paint={circlePaint(colors.duc)} // Orange for DUC
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalDuc && (
          <Layer
            id="wells-v2-horizontal-duc-bottom"
            type="circle"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, ducFilter)}
            paint={circlePaint(colors.duc)} // Orange for DUC
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalProducing && (
          <Layer
            id="wells-v2-horizontal-producing"
            type="circle"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, producingFilter)}
            paint={circlePaint(colors.producing)} // Blue for producing
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalProducing && (
          <Layer
            id="wells-v2-horizontal-producing-bottom"
            type="circle"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, producingFilter)}
            paint={circlePaint(colors.producing)} // Blue for producing
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalShutIn && (
          <Layer
            id="wells-v2-horizontal-shut-in"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, shutInFilter)}
            layout={{
              'text-field': '×',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.shutIn, // Darker gray for shut in
            }}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalShutIn && (
          <Layer
            id="wells-v2-horizontal-shut-in-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, shutInFilter)}
            layout={{
              'text-field': '×',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.shutIn, // Darker gray for shut in
            }}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalExpiredPermit && (
          <Layer
            id="wells-v2-horizontal-expired"
            type="circle"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, expiredPermitFilter)}
            paint={circlePaint(colors.expiredPermit)} // Gray for expired
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalExpiredPermit && (
          <Layer
            id="wells-v2-horizontal-expired-bottom"
            type="circle"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, expiredPermitFilter)}
            paint={circlePaint(colors.expiredPermit)} // Gray for expired
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalInactive && (
          <Layer
            id="wells-v2-horizontal-inactive"
            type="circle"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, inactiveFilter)}
            paint={circlePaint(colors.inactive)} // Lighter gray for inactive
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalInactive && (
          <Layer
            id="wells-v2-horizontal-inactive-bottom"
            type="circle"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, inactiveFilter)}
            paint={circlePaint(colors.inactive)} // Lighter gray for inactive
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalPrePermit && (
          <Layer
            id="wells-v2-horizontal-pre-permit"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, prePermitFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.prePermit)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalPrePermit && (
          <Layer
            id="wells-v2-horizontal-pre-permit-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, prePermitFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.prePermit)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalApd && (
          <Layer
            id="wells-v2-horizontal-apd"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, apdFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.apd)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalProposal && (
          <Layer
            id="wells-v2-horizontal-proposal"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, proposalFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.proposal)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalPlanned && (
          <Layer
            id="wells-v2-horizontal-planned"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(horizontalFilter, plannedFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.planned)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalApd && (
          <Layer
            id="wells-v2-horizontal-apd-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, apdFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.apd)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalProposal && (
          <Layer
            id="wells-v2-horizontal-proposal-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, proposalFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.proposal)}
          />
        )}

        {isHorizontalEnabled && wellsV2Options.horizontalPlanned && (
          <Layer
            id="wells-v2-horizontal-planned-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(horizontalFilter, plannedFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.planned)}
          />
        )}

        {/* Vertical Wells by Status */}
        {isVerticalEnabled && wellsV2Options.verticalPermit && (
          <Layer
            id="wells-v2-vertical-permit"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, permitFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.permit, // Pink for permit
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalPermit && (
          <Layer
            id="wells-v2-vertical-permit-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, permitFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.permit, // Pink for permit
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalDuc && (
          <Layer
            id="wells-v2-vertical-duc"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, ducFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.duc, // Orange for DUC
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalDuc && (
          <Layer
            id="wells-v2-vertical-duc-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, ducFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.duc, // Orange for DUC
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalProducing && (
          <Layer
            id="wells-v2-vertical-producing"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, producingFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.producing, // Blue for producing
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalProducing && (
          <Layer
            id="wells-v2-vertical-producing-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, producingFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.producing, // Blue for producing
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalShutIn && (
          <Layer
            id="wells-v2-vertical-shut-in"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, shutInFilter)}
            layout={{
              'text-field': '×',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.shutIn, // Darker gray for shut in
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalShutIn && (
          <Layer
            id="wells-v2-vertical-shut-in-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, shutInFilter)}
            layout={{
              'text-field': '×',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.shutIn, // Darker gray for shut in
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalExpiredPermit && (
          <Layer
            id="wells-v2-vertical-expired"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, expiredPermitFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.expiredPermit, // Gray for expired
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalExpiredPermit && (
          <Layer
            id="wells-v2-vertical-expired-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, expiredPermitFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.expiredPermit, // Gray for expired
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalInactive && (
          <Layer
            id="wells-v2-vertical-inactive"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, inactiveFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.inactive, // Lighter gray for inactive
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalInactive && (
          <Layer
            id="wells-v2-vertical-inactive-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, inactiveFilter)}
            layout={{
              'text-field': '●',
              'text-allow-overlap': true,
              'text-size': textSizeInterpolate,
            }}
            paint={{
              'text-color': colors.inactive, // Lighter gray for inactive
            }}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalPrePermit && (
          <Layer
            id="wells-v2-vertical-pre-permit"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, prePermitFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.prePermit)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalPrePermit && (
          <Layer
            id="wells-v2-vertical-pre-permit-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, prePermitFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.prePermit)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalApd && (
          <Layer
            id="wells-v2-vertical-apd"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, apdFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.apd)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalProposal && (
          <Layer
            id="wells-v2-vertical-proposal"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, proposalFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.proposal)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalPlanned && (
          <Layer
            id="wells-v2-vertical-planned"
            type="symbol"
            source-layer="surface-hole"
            filter={createCombinedFilter(verticalFilter, plannedFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.planned)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalApd && (
          <Layer
            id="wells-v2-vertical-apd-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, apdFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.apd)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalProposal && (
          <Layer
            id="wells-v2-vertical-proposal-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, proposalFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.proposal)}
          />
        )}

        {isVerticalEnabled && wellsV2Options.verticalPlanned && (
          <Layer
            id="wells-v2-vertical-planned-bottom"
            type="symbol"
            source-layer="bottom-hole"
            filter={createCombinedFilter(verticalFilter, plannedFilter)}
            layout={prePermitHexagonLayout}
            paint={prePermitHexagonPaint(colors.planned)}
          />
        )}

        {/* Survey lines for vertical wells */}
        {isVerticalEnabled && (
          <Layer
            id="wells-v2-vertical-survey"
            type="line"
            source-layer="survey"
            filter={['all', verticalSurveyFilter, measuredSurvey]}
            paint={surveyPaint}
          />
        )}
        {isVerticalEnabled && (
          <Layer
            id="wells-v2-vertical-survey-estimated"
            type="line"
            source-layer="survey"
            filter={['all', verticalSurveyFilter, estimatedSurvey]}
            paint={estimatedSurveyPaint}
          />
        )}

        {/* Well names along survey lines for vertical wells - always show */}
        {isVerticalEnabled && (
          <Layer
            id="wells-v2-vertical-survey-names"
            type="symbol"
            source-layer="survey"
            filter={[
              'all',
              verticalFilter,
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.verticalPermit ? [permitFilter] : []),
                ...(wellsV2Options.verticalDuc ? [ducFilter] : []),
                ...(wellsV2Options.verticalProducing ? [producingFilter] : []),
                ...(wellsV2Options.verticalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.verticalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.verticalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.verticalPrePermit ? [prePermitFilter] : []),
                ...(wellsV2Options.verticalApd ? [apdFilter] : []),
                ...(wellsV2Options.verticalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.verticalPlanned ? [plannedFilter] : []),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />
        )}

        {/* Stick lines for vertical wells that don't have survey */}
        {isVerticalEnabled && (
          <Layer
            id="wells-v2-vertical-stick"
            type="line"
            source-layer="stick"
            filter={[
              'all',
              verticalFilter,
              ['==', 'has_survey', false],
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.verticalPermit ? [permitFilter] : []),
                ...(wellsV2Options.verticalDuc ? [ducFilter] : []),
                ...(wellsV2Options.verticalProducing ? [producingFilter] : []),
                ...(wellsV2Options.verticalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.verticalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.verticalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.verticalPrePermit ? [prePermitFilter] : []),
                ...(wellsV2Options.verticalApd ? [apdFilter] : []),
                ...(wellsV2Options.verticalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.verticalPlanned ? [plannedFilter] : []),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                mapColors.stickFill, // default color if none match
              ],
              'line-width': 1.5,
              'line-dasharray': [6, 2],
            }}
          />
        )}

        {/* Well names along stick lines for vertical wells - always show */}
        {isVerticalEnabled && (
          <Layer
            id="wells-v2-vertical-stick-names"
            type="symbol"
            source-layer="stick"
            filter={[
              'all',
              verticalFilter,
              ['==', 'has_survey', false],
              [
                'any',
                // Only include wells with statuses that are enabled
                ...(wellsV2Options.verticalPermit ? [permitFilter] : []),
                ...(wellsV2Options.verticalDuc ? [ducFilter] : []),
                ...(wellsV2Options.verticalProducing ? [producingFilter] : []),
                ...(wellsV2Options.verticalShutIn ? [shutInFilter] : []),
                ...(wellsV2Options.verticalExpiredPermit
                  ? [expiredPermitFilter]
                  : []),
                ...(wellsV2Options.verticalInactive ? [inactiveFilter] : []),
                ...(wellsV2Options.verticalPrePermit ? [prePermitFilter] : []),
                ...(wellsV2Options.verticalApd ? [apdFilter] : []),
                ...(wellsV2Options.verticalProposal ? [proposalFilter] : []),
                ...(wellsV2Options.verticalPlanned ? [plannedFilter] : []),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />
        )}
      </Source>
    </LayerBand>
  );
};

export { WellsV2Layer };
